import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AUDITED_READ_OPTIONS } from "@/shared/api/audited-read";
import { httpClient } from "@/shared/api/http-client";
import type { Paged } from "@/shared/types/paged";
import {
  ORGANIZATION_LOCATIONS_PAGE_SIZE,
  ORGANIZATION_LOCATIONS_SEARCH_MAX_LENGTH,
} from "./location-limits";
import { normalizeOrganizationId } from "./organization-id";
import type { OrganizationKind } from "./organization-link";

export interface OrganizationLocation {
  readonly id: string;
  readonly name: string;
}

/** One row of `GET /api/admin/organizations/:id/locations` (`AssignableLocationModel`). */
export interface AssignableLocation extends OrganizationLocation {
  readonly kind: OrganizationKind;
}

/** Prefix of every cached page of one organization's locations (all searches, all pages). */
export function accessLocationsKey(organizationId: string) {
  return [
    "admin",
    "organizations",
    "access-locations",
    normalizeOrganizationId(organizationId),
  ] as const;
}

export interface OrganizationLocationsParams {
  readonly organizationId: string | null;
  /** Keeps the read from firing until the checklist is actually shown. */
  readonly enabled: boolean;
  readonly search: string;
  /** 1-based. */
  readonly page: number;
}

export interface OrganizationLocations {
  readonly locations: readonly OrganizationLocation[];
  readonly totalCount: number;
  readonly hasNextPage: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
  /** A (re)read is running: with `keepPreviousData` the rows on screen may be the previous page's. */
  readonly isFetching: boolean;
  readonly refetch: () => void;
}

/**
 * One page of the locations an operator may assign inside ONE organization:
 * `GET /api/admin/organizations/:id/locations?page&limit&search` (the venues of
 * a VENUE organization, the properties of a PROPERTY one; id, name and kind
 * only, `org.members.manage`). A single read: the organization decides the
 * kind, so the dialogs never need the owner's account.
 */
export function useOrganizationLocations({
  organizationId,
  enabled,
  search,
  page,
}: OrganizationLocationsParams): OrganizationLocations {
  const id = organizationId === null ? null : normalizeOrganizationId(organizationId);
  const term = search.trim().slice(0, ORGANIZATION_LOCATIONS_SEARCH_MAX_LENGTH).trim();
  const query = useQuery({
    queryKey: ["admin", "organizations", "access-locations", id, term, page] as const,
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(ORGANIZATION_LOCATIONS_PAGE_SIZE),
        ...(term ? { search: term } : {}),
      });
      return httpClient<Paged<AssignableLocation>>(
        `/api/admin/organizations/${id}/locations?${params}`,
      );
    },
    enabled: enabled && id !== null,
    placeholderData: keepPreviousData,
    ...AUDITED_READ_OPTIONS,
  });

  const data = query.data;
  return {
    locations: (data?.items ?? []).map(({ id: locationId, name }) => ({ id: locationId, name })),
    totalCount: data?.totalCount ?? 0,
    hasNextPage: data !== undefined && data.page * data.limit < data.totalCount,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: () => void query.refetch(),
  };
}
